/**
 * expo-router compatibility shim, backed by @react-navigation/native.
 *
 * Every file that used to `import { ... } from 'expo-router'` now imports
 * from here instead.  The goal is that individual screen files do NOT
 * need to change their call sites — `router.push('/owner/dashboard')`,
 * `useLocalSearchParams<{id:string}>()`, `<Stack.Screen options={...} />`
 * and `<Redirect href="/login" />` all keep working.
 *
 * This shim understands the following expo-router surface area, which
 * is everything the Rinsepay app actually uses:
 *
 *   • useRouter()           → object with push / replace / back /
 *                              dismissAll / setParams / navigate /
 *                              canGoBack
 *   • router                → the same object, usable outside React
 *                              components (backed by navigationRef)
 *   • useLocalSearchParams<T>()   → typed params object for the active route
 *   • useFocusEffect(cb)    → react-navigation's native useFocusEffect,
 *                              re-exported verbatim
 *   • useSegments()         → array of path segments for the active route
 *                              (derived from the current screen name)
 *   • useNavigation()       → raw react-navigation navigation object
 *   • <Stack>               → inert passthrough (layout files that used
 *                              <Stack>...</Stack> now render a Fragment;
 *                              real stack wiring lives in RootNavigator)
 *   • <Stack.Screen options={...} />
 *                           → applies options to the parent screen via
 *                              navigation.setOptions; rendered as null
 *   • <Redirect href="..." />
 *                           → navigates once on mount, renders null
 *   • Link                  → simple Touchable wrapper (unused in-app
 *                              today but exported for safety)
 *
 * Path parsing:
 *   Path strings like '/owner/location/42' or '/wash/abc/xyz' are resolved
 *   by scanning the static screen registry in src/navigation/screens.ts,
 *   picking the most specific route whose URL pattern matches.  Dynamic
 *   segments ([id], [locationId], etc.) are extracted into the params
 *   object.  Query strings on the href are merged into params as well.
 *
 *   This is intentionally simpler than react-navigation's own
 *   getStateFromPath because we only ever have a single flat stack — we
 *   don't need nested-navigator state reconstruction.
 */
import React, { useEffect, useMemo } from 'react';
import { TouchableOpacity, Text } from 'react-native';
import {
  useNavigation as rnUseNavigation,
  useRoute,
  useFocusEffect as rnUseFocusEffect,
  StackActions,
  CommonActions,
} from '@react-navigation/native';
import type { ParamListBase, NavigationProp } from '@react-navigation/native';
import { navigationRef } from '../navigation/navigationRef';
import { SCREENS } from '../navigation/screens';

// ---------------------------------------------------------------------------
// path resolution
// ---------------------------------------------------------------------------

type Href =
  | string
  | {
      pathname: string;
      params?: Record<string, string | number | boolean | undefined | null>;
    };

interface ResolvedRoute {
  screenName: string;
  params: Record<string, string>;
}

/** Split "/foo/bar?x=1&y=2" into "/foo/bar" and { x:'1', y:'2' } */
function splitQuery(path: string): { path: string; query: Record<string, string> } {
  const qIdx = path.indexOf('?');
  if (qIdx === -1) return { path, query: {} };
  const base = path.slice(0, qIdx);
  const qs = path.slice(qIdx + 1);
  const query: Record<string, string> = {};
  for (const pair of qs.split('&')) {
    if (!pair) continue;
    const eq = pair.indexOf('=');
    if (eq === -1) query[decodeURIComponent(pair)] = '';
    else query[decodeURIComponent(pair.slice(0, eq))] = decodeURIComponent(pair.slice(eq + 1));
  }
  return { path: base, query };
}

/**
 * Try to match a URL path against a screen's stored path pattern.
 * Returns the extracted params if it matches, or null if not.
 *
 * Patterns look like "owner/location/:id" or "wash/:locationId/:bayId"
 * or plain strings like "owner/dashboard".
 */
function matchPattern(
  pattern: string,
  segments: string[],
): Record<string, string> | null {
  const patternSegs = pattern.split('/').filter(Boolean);
  if (patternSegs.length !== segments.length) return null;
  const out: Record<string, string> = {};
  for (let i = 0; i < patternSegs.length; i++) {
    const p = patternSegs[i];
    if (p.startsWith(':')) {
      out[p.slice(1)] = decodeURIComponent(segments[i]);
    } else if (p !== segments[i]) {
      return null;
    }
  }
  return out;
}

/**
 * Resolve a path string (or object-form href) to a { screenName, params }
 * pair by scanning the screen registry.
 *
 * Matching strategy:
 *   1. Strip leading '/', split off query string, split path into segments.
 *   2. Walk the screen registry in a deterministic order and keep the FIRST
 *      pattern that matches.  Static patterns (no ':') sort before dynamic
 *      ones so that e.g. "owner/location/edit" wins over
 *      "owner/location/:id".
 *   3. If nothing matches, fall back to the '*' catch-all (not-found).
 */
export function resolveHref(href: Href): ResolvedRoute {
  let rawPath: string;
  let extraParams: Record<string, any> = {};
  if (typeof href === 'string') {
    rawPath = href;
  } else {
    rawPath = href.pathname;
    if (href.params) {
      for (const [k, v] of Object.entries(href.params)) {
        if (v != null) extraParams[k] = String(v);
      }
    }
  }
  // Strip leading slash
  if (rawPath.startsWith('/')) rawPath = rawPath.slice(1);
  const { path, query } = splitQuery(rawPath);
  const segments = path.split('/').filter(Boolean);

  // Empty path → root index
  if (segments.length === 0) {
    return { screenName: 'index', params: { ...query, ...extraParams } };
  }

  // Prefer more specific (static) routes over parameterized ones.
  const ordered = [...SCREENS].sort((a, b) => {
    const aDyn = (a.path.match(/:/g) || []).length;
    const bDyn = (b.path.match(/:/g) || []).length;
    if (aDyn !== bDyn) return aDyn - bDyn;
    // longer paths before shorter to prefer exact prefixes
    return b.path.length - a.path.length;
  });

  for (const screen of ordered) {
    if (screen.path === '*') continue; // handled as fallback
    const matched = matchPattern(screen.path, segments);
    if (matched) {
      return { screenName: screen.name, params: { ...matched, ...query, ...extraParams } };
    }
  }

  return { screenName: 'not-found', params: { ...query, ...extraParams } };
}

// ---------------------------------------------------------------------------
// router singleton
// ---------------------------------------------------------------------------

function dispatchPush(href: Href) {
  if (!navigationRef.isReady()) return;
  const { screenName, params } = resolveHref(href);
  navigationRef.dispatch(StackActions.push(screenName, params));
}

function dispatchReplace(href: Href) {
  if (!navigationRef.isReady()) return;
  const { screenName, params } = resolveHref(href);
  navigationRef.dispatch(StackActions.replace(screenName, params));
}

function dispatchNavigate(href: Href) {
  if (!navigationRef.isReady()) return;
  const { screenName, params } = resolveHref(href);
  navigationRef.dispatch(CommonActions.navigate({ name: screenName, params }));
}

export interface ExpoRouterLike {
  push: (href: Href) => void;
  replace: (href: Href) => void;
  navigate: (href: Href) => void;
  back: () => void;
  dismissAll: () => void;
  canGoBack: () => boolean;
  setParams: (params: Record<string, any>) => void;
}

/** Imperative router — usable outside React components. */
export const router: ExpoRouterLike = {
  push: dispatchPush,
  replace: dispatchReplace,
  navigate: dispatchNavigate,
  back: () => { if (navigationRef.isReady() && navigationRef.canGoBack()) navigationRef.goBack(); },
  dismissAll: () => { if (navigationRef.isReady()) navigationRef.dispatch(StackActions.popToTop()); },
  canGoBack: () => navigationRef.isReady() && navigationRef.canGoBack(),
  setParams: (params) => {
    if (navigationRef.isReady()) navigationRef.dispatch(CommonActions.setParams(params));
  },
};

// ---------------------------------------------------------------------------
// hooks
// ---------------------------------------------------------------------------

/** Mirrors expo-router's useRouter().  Returns a stable object-ish API. */
export function useRouter(): ExpoRouterLike {
  const nav = rnUseNavigation<NavigationProp<ParamListBase>>();
  return useMemo<ExpoRouterLike>(() => ({
    push: (href) => {
      const { screenName, params } = resolveHref(href);
      // @ts-expect-error navigation.push exists on stack navigators
      nav.push ? nav.push(screenName, params) : nav.navigate(screenName as any, params);
    },
    replace: (href) => {
      const { screenName, params } = resolveHref(href);
      nav.dispatch(StackActions.replace(screenName, params));
    },
    navigate: (href) => {
      const { screenName, params } = resolveHref(href);
      nav.navigate(screenName as any, params);
    },
    back: () => { if (nav.canGoBack()) nav.goBack(); },
    dismissAll: () => { nav.dispatch(StackActions.popToTop()); },
    canGoBack: () => nav.canGoBack(),
    setParams: (params) => nav.setParams(params as any),
  }), [nav]);
}

/** Returns the route params for the current screen, typed. */
export function useLocalSearchParams<T extends Record<string, any> = Record<string, string>>(): T {
  const route = useRoute();
  return (route.params as T) || ({} as T);
}

/** Re-export react-navigation's useFocusEffect — identical signature. */
export const useFocusEffect = rnUseFocusEffect;

/** Re-export useNavigation verbatim for the handful of screens that used it. */
export const useNavigation = rnUseNavigation;

/**
 * expo-router's useSegments() returns an array of path segments for the
 * currently active route, excluding dynamic params.  We derive it from
 * the current screen name by splitting on '/' and dropping any segment
 * that looks like '[...]'.
 */
export function useSegments(): string[] {
  const route = useRoute();
  return useMemo(() => {
    const name = route.name || '';
    const segs = name
      .split('/')
      .filter(Boolean)
      .filter((seg) => !(seg.startsWith('[') && seg.endsWith(']')));
    // Trailing 'index' is the directory's own route in expo-router, not a
    // child segment — drop it so `/owner/fleet/index` reads as ['owner','fleet'].
    if (segs.length > 1 && segs[segs.length - 1] === 'index') segs.pop();
    return segs;
  }, [route.name]);
}

// ---------------------------------------------------------------------------
// Stack / Stack.Screen
// ---------------------------------------------------------------------------

interface StackScreenProps {
  name?: string;
  options?: Record<string, any>;
  children?: React.ReactNode;
}

/**
 * <Stack.Screen options={...} /> — historically rendered inside a screen
 * body to tweak its own header options.  We reproduce that by calling
 * navigation.setOptions in an effect.  The `name` prop from layout-level
 * usage is ignored (layout files are no longer wired up this way; the
 * real stack is in RootNavigator).
 */
function StackScreen(props: StackScreenProps): null {
  const nav = rnUseNavigation();
  const { options } = props;
  useEffect(() => {
    if (options) {
      // setOptions is a no-op if we're not on an active screen
      try { nav.setOptions(options as any); } catch { /* non-critical */ }
    }
  }, [nav, options]);
  return null;
}

interface StackProps {
  children?: React.ReactNode;
  screenOptions?: Record<string, any>;
}

/**
 * <Stack> used to declare a navigator at the top of a _layout.tsx file.
 * In the bare-RN world the navigator is built by RootNavigator, so this
 * is an inert passthrough — render children as a fragment.
 */
function StackImpl(props: StackProps) {
  return <>{props.children}</>;
}

// Attach the .Screen sub-component so `<Stack.Screen />` keeps working.
export const Stack = Object.assign(StackImpl, { Screen: StackScreen });

// ---------------------------------------------------------------------------
// Redirect
// ---------------------------------------------------------------------------

interface RedirectProps {
  href: string;
}

/**
 * <Redirect href="/login" /> — fires once on mount, replaces the current
 * screen with the target.  Used as an auth-guard pattern in several
 * _layout files and in app/+not-found.tsx.
 */
export function Redirect({ href }: RedirectProps): null {
  useEffect(() => {
    router.replace(href);
  }, [href]);
  return null;
}

// ---------------------------------------------------------------------------
// Link (rarely used, but exported for completeness)
// ---------------------------------------------------------------------------

interface LinkProps {
  href: string;
  children?: React.ReactNode;
  style?: any;
  asChild?: boolean;
}

export function Link({ href, children, style }: LinkProps) {
  return (
    <TouchableOpacity onPress={() => router.push(href)} style={style}>
      {typeof children === 'string' ? <Text>{children}</Text> : children}
    </TouchableOpacity>
  );
}
